import { createAction } from "redux-actions";
import {
  TRANSACTION,
  FS_CREATE_ITEMS,
  FS_DELETE_ITEMS,
  FS_UPDATE_ITEMS,
} from "../common";
import {
  addTransaction,
  deleteTransaction,
  deleteTransactions,
  deleteSplitGroup,
  setSplitGroupReceipt,
  unsplitTransaction,
  updateTransactionDescription,
  upsertSplitTransaction,
  type MappedOption,
  type SplitCommonFields,
  type SplitRowInput,
} from "src/apiService/modules/transactions";
import type { AppState } from "src/store";
import type { AppDispatch } from "src/store/types";
import type {
  Transaction,
  TransactionWithDetails,
} from "src/interfaces/transaction.interface";
import { withOptimisticUpdate } from "src/store/utils/withOptimisticUpdate";
import { transactionByIdSelector } from "src/store/transaction/selector";

export const addTransactionRecords = createAction(
  TRANSACTION + FS_CREATE_ITEMS
);
export const updateTransactionRecords = createAction(
  TRANSACTION + FS_UPDATE_ITEMS
);
export const deleteTransactionRecords = createAction(
  TRANSACTION + FS_DELETE_ITEMS
);
export const SET_OLDEST_TRANSACTION_YEAR = "SET_OLDEST_TRANSACTION_YEAR";
export const setOldestTransactionYear = createAction<number | null>(
  SET_OLDEST_TRANSACTION_YEAR
);

export function updateTransactionDescriptionOptimistic(
  transactionId: string,
  description: string
) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    const previousRecord = transactionByIdSelector(getState(), transactionId);

    await withOptimisticUpdate({
      apply: () => {
        if (previousRecord) {
          dispatch(
            updateTransactionRecords([
              {
                ...previousRecord,
                description,
              },
            ])
          );
        }
      },
      revert: () => {
        if (previousRecord) {
          dispatch(updateTransactionRecords([previousRecord]));
        }
      },
      commit: () => updateTransactionDescription(transactionId, description),
    });
  };
}

/**
 * Optimistically patches (or creates after commit) a transaction, then writes Firestore.
 * Rolls Redux back if the write fails. Callers own toasts.
 */
export function saveTransactionOptimistic(
  transaction: Partial<Transaction>,
  options: { saleId?: string; mapped?: MappedOption } = {},
  newImageFile?: File
) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    const previous = transaction.id
      ? transactionByIdSelector(getState(), transaction.id)
      : undefined;

    const { id, receipt_filename } = await withOptimisticUpdate({
      apply: () => {
        if (!transaction.id) return;
        const next = {
          ...(previous || {}),
          ...transaction,
          id: transaction.id,
        } as TransactionWithDetails;
        if ("vendor" in transaction && transaction.vendor == null) {
          delete next.vendor;
          delete next.vendorName;
          delete next.vendorDoc;
        }
        dispatch(updateTransactionRecords([next]));
      },
      revert: () => {
        if (!transaction.id) return;
        if (previous) dispatch(updateTransactionRecords([previous]));
        else dispatch(deleteTransactionRecords([{ id: transaction.id }]));
      },
      commit: () => addTransaction(transaction, options, newImageFile),
    });

    if (!transaction.id && id) {
      dispatch(
        addTransactionRecords([
          {
            ...transaction,
            id,
            ...(receipt_filename && { receipt_filename }),
          },
        ])
      );
    } else if (transaction.id && receipt_filename) {
      const current = itemsById(getState())[transaction.id];
      if (current) {
        dispatch(updateTransactionRecords([{ ...current, receipt_filename }]));
      }
    }

    return id;
  };
}

export function deleteTransactionOptimistic(id: string) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    const previous = transactionByIdSelector(getState(), id);

    await withOptimisticUpdate({
      apply: () => {
        dispatch(deleteTransactionRecords([{ id }]));
      },
      revert: () => {
        if (previous) dispatch(addTransactionRecords([previous]));
      },
      commit: () => deleteTransaction(id),
    });
  };
}

function itemsById(state: AppState): Record<string, Transaction> {
  return state.transaction?.itemsById ?? {};
}

/**
 * Rows `deleteTransactions` will remove that are already in Redux.
 * Read-only rows are left in place. Split siblings are included from the
 * store; the commit still expands any siblings that are not loaded.
 */
function rowsRemovedByDelete(
  items: Record<string, Transaction>,
  ids: string[]
): Transaction[] {
  const removed = new Map<string, Transaction>();

  const consider = (row: Transaction | undefined) => {
    if (!row?.id || row.read_only || removed.has(row.id)) return;
    removed.set(row.id, row);
    if (!row.split_group_id) return;
    for (const other of Object.values(items)) {
      if (other.split_group_id === row.split_group_id) consider(other);
    }
  };

  for (const id of ids) consider(items[id]);

  // deleteTransaction also deletes every row in the journal entry. Those
  // siblings are not always split rows, and read-only journal lines are
  // included because deleteJournalEntry does not skip them.
  const journalIds = new Set<string>();
  for (const row of removed.values()) {
    if (row.journal_entry_id) journalIds.add(row.journal_entry_id);
  }
  if (journalIds.size) {
    for (const other of Object.values(items)) {
      if (!other?.id || !other.journal_entry_id) continue;
      if (journalIds.has(other.journal_entry_id)) removed.set(other.id, other);
    }
  }

  return [...removed.values()];
}

function groupRows(
  items: Record<string, Transaction>,
  splitGroupId: string | undefined
): Transaction[] {
  if (!splitGroupId) return [];
  return Object.values(items).filter(
    (row) => row.split_group_id === splitGroupId && !row.read_only
  );
}

function withoutDerived(
  row: Partial<Transaction> | undefined
): Partial<Transaction> {
  if (!row) return {};
  const next = { ...row } as Partial<Transaction> & {
    accountDoc?: unknown;
    vendorDoc?: unknown;
    vendorName?: unknown;
    vector?: unknown;
  };
  delete next.accountDoc;
  delete next.vendorDoc;
  delete next.vendorName;
  delete next.vector;
  return next;
}

function buildOptimisticSplitRow(
  previous: Transaction | undefined,
  row: SplitRowInput & { id: string },
  common: SplitCommonFields,
  splitGroupId: string | undefined,
  receiptFilename?: string | null
): Transaction {
  const next: Transaction = {
    ...(withoutDerived(previous) as Transaction),
    ...common,
    id: row.id,
    amount: row.amount,
    gl_account_id: row.gl_account_id,
    account: row.account,
  };
  if (row.description !== undefined) next.description = row.description;
  if (splitGroupId) next.split_group_id = splitGroupId;
  else if (!previous?.split_group_id) delete next.split_group_id;
  if (receiptFilename) next.receipt_filename = receiptFilename;
  else if (receiptFilename === null) delete next.receipt_filename;
  return next;
}

/**
 * Optimistically removes every loaded row `deleteTransactions` will delete,
 * including split siblings already in the store, then commits through
 * `deleteTransactions`. Restores those rows if the commit throws.
 */
export function deleteTransactionsOptimistic(ids: string[]) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    if (!ids.length) return;

    const previous = rowsRemovedByDelete(itemsById(getState()), ids);

    await withOptimisticUpdate({
      apply: () => {
        if (previous.length) {
          dispatch(
            deleteTransactionRecords(previous.map((row) => ({ id: row.id })))
          );
        }
      },
      revert: () => {
        if (previous.length) dispatch(addTransactionRecords(previous));
      },
      commit: () => deleteTransactions(ids),
    });
  };
}

/**
 * Patches loaded split rows (and drops rows the save removes) before
 * `upsertSplitTransaction` returns. New server ids are applied before this
 * thunk returns. Reverts the patch if the commit throws.
 */
export function upsertSplitTransactionOptimistic(args: {
  split_group_id?: string;
  common: SplitCommonFields;
  rows: SplitRowInput[];
  newImageFile?: File;
  existingReceiptFilename?: string | null;
}) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    const items = itemsById(getState());
    const previousById = new Map<string, Transaction>();
    for (const row of groupRows(items, args.split_group_id)) {
      previousById.set(row.id, row);
    }
    for (const row of args.rows) {
      if (!row.id || previousById.has(row.id) || !items[row.id]) continue;
      if (items[row.id].read_only) continue;
      previousById.set(row.id, items[row.id]);
    }

    const keptIds = new Set(
      args.rows.map((row) => row.id).filter((id): id is string => !!id)
    );
    const removed = [...previousById.values()].filter(
      (row) => !keptIds.has(row.id)
    );
    // Same rule the server uses: a new file gets a path back from the save,
    // otherwise an explicit existing filename (or null) is written as-is.
    const keptReceipt = args.newImageFile
      ? undefined
      : args.existingReceiptFilename;
    const optimisticExisting = args.rows
      .filter(
        (row): row is SplitRowInput & { id: string } =>
          !!row.id && !items[row.id]?.read_only
      )
      .map((row) =>
        buildOptimisticSplitRow(
          previousById.get(row.id),
          row,
          args.common,
          args.split_group_id,
          keptReceipt
        )
      );

    return await withOptimisticUpdate({
      apply: () => {
        if (removed.length) {
          dispatch(
            deleteTransactionRecords(removed.map((row) => ({ id: row.id })))
          );
        }
        if (optimisticExisting.length) {
          dispatch(updateTransactionRecords(optimisticExisting));
        }
      },
      revert: () => {
        const restored: Transaction[] = [];
        const addedIds: string[] = [];
        for (const row of optimisticExisting) {
          const previous = previousById.get(row.id);
          if (previous) restored.push(previous);
          else addedIds.push(row.id);
        }
        if (restored.length) dispatch(updateTransactionRecords(restored));
        if (addedIds.length) {
          dispatch(deleteTransactionRecords(addedIds.map((id) => ({ id }))));
        }
        if (removed.length) dispatch(addTransactionRecords(removed));
      },
      commit: async () => {
        const result = await upsertSplitTransaction(args);
        const writtenReceipt = result.receipt_filename ?? keptReceipt;
        const written = args.rows.flatMap((row, index) => {
          const id = result.ids[index];
          if (!id || items[id]?.read_only) return [];
          return [
            buildOptimisticSplitRow(
              previousById.get(id) ||
                (row.id ? previousById.get(row.id) : undefined),
              {
                ...row,
                id,
              },
              args.common,
              result.split_group_id,
              writtenReceipt
            ),
          ];
        });
        const updates = written.filter((row) => keptIds.has(row.id));
        const adds = written.filter((row) => !keptIds.has(row.id));
        if (updates.length) dispatch(updateTransactionRecords(updates));
        if (adds.length) dispatch(addTransactionRecords(adds));
        return result;
      },
    });
  };
}

function buildUnsplitRow(
  previous: Transaction | undefined,
  fields: Partial<Transaction>,
  id: string
): Transaction {
  const next: Transaction = {
    ...(withoutDerived(previous) as Transaction),
    ...fields,
    id,
  };
  delete next.split_group_id;
  return next;
}

/**
 * Shows the collapsed expense on a loaded group row before `unsplitTransaction`
 * returns, then swaps in the server id. Restores the group if the commit throws.
 */
export function unsplitTransactionOptimistic(
  splitGroupId: string,
  transaction: Partial<Transaction>,
  newImageFile?: File
) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    const group = groupRows(itemsById(getState()), splitGroupId);
    const standIn = group[0];
    const others = standIn ? group.filter((row) => row.id !== standIn.id) : [];
    const standInNext = standIn
      ? buildUnsplitRow(standIn, transaction, standIn.id)
      : undefined;

    return await withOptimisticUpdate({
      apply: () => {
        if (standInNext) dispatch(updateTransactionRecords([standInNext]));
        if (others.length) {
          dispatch(
            deleteTransactionRecords(others.map((row) => ({ id: row.id })))
          );
        }
      },
      revert: () => {
        if (standIn) dispatch(updateTransactionRecords([standIn]));
        if (others.length) dispatch(addTransactionRecords(others));
      },
      commit: async () => {
        const newId = await unsplitTransaction(
          splitGroupId,
          transaction,
          newImageFile
        );
        if (standIn) dispatch(deleteTransactionRecords([{ id: standIn.id }]));
        dispatch(
          addTransactionRecords([buildUnsplitRow(standIn, transaction, newId)])
        );
        return newId;
      },
    });
  };
}

/**
 * Saves a split group's receipt (or clears it without a file), then patches
 * the rows the server wrote. Returns the stored receipt path.
 */
export function saveSplitGroupReceipt(
  splitGroupId: string,
  primaryId: string,
  receiptFile?: File
) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    const { ids, receipt_filename } = await setSplitGroupReceipt(
      splitGroupId,
      primaryId,
      receiptFile
    );
    const items = itemsById(getState());
    const written = ids.flatMap((id) => {
      if (!items[id]) return [];
      const next: Transaction = { ...items[id] };
      if (receipt_filename) next.receipt_filename = receipt_filename;
      else delete next.receipt_filename;
      return [next];
    });
    if (written.length) dispatch(updateTransactionRecords(written));
    return receipt_filename;
  };
}

/** Removes loaded split rows before `deleteSplitGroup` returns. Restores them on failure. */
export function deleteSplitGroupOptimistic(splitGroupId: string) {
  return async (dispatch: AppDispatch, getState: () => AppState) => {
    const previous = groupRows(itemsById(getState()), splitGroupId);

    await withOptimisticUpdate({
      apply: () => {
        if (previous.length) {
          dispatch(
            deleteTransactionRecords(previous.map((row) => ({ id: row.id })))
          );
        }
      },
      revert: () => {
        if (previous.length) dispatch(addTransactionRecords(previous));
      },
      commit: () => deleteSplitGroup(splitGroupId),
    });
  };
}
